import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";


import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { ProjetoRevisaoResumoForm, ProjetoRevisaoResumoSchema } from "../../../schemas/Projetos_cadastro/projeto_revisao";
import { DropDownFuncionario } from "../../../../../../common/components/dropdown_funcionario";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";


type Props = {
  open: boolean,
  saving: boolean,
  selectedRevisao: ProjetoRevisaoResumoForm | null,
  IdDisciplina: number;
  onSubmit: (form: ProjetoRevisaoResumoForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onClose: () => void,
};

export const ProjetoRevisaoModal = ({
  open,
  saving,
  IdDisciplina,
  selectedRevisao,
  onOpenChangeHistory,
  onSubmit,
  onClose,
}: Props) => {
  const hookForm = useForm<ProjetoRevisaoResumoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ProjetoRevisaoResumoSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedRevisao) {
        hookForm.reset({
          idProjetoRevisaoResumo: selectedRevisao.idProjetoRevisaoResumo ?? undefined,
          idProjeto: selectedRevisao.idProjeto ?? null,
          idDisciplina: selectedRevisao.idDisciplina ?? null,
          descricao: selectedRevisao.descricao ?? "",
          responsavel: selectedRevisao.responsavel ?? null,
          idFuncionario: selectedRevisao.idFuncionario ?? null,
          data: selectedRevisao.data ?? undefined,
        });
      } else {
        hookForm.reset({
          idProjetoRevisaoResumo: undefined,
          idDisciplina: IdDisciplina,
          descricao: "",
          responsavel: null,
          idFuncionario: null,
          data: undefined,
        });
      }
    }
  }, [IdDisciplina, hookForm, open, selectedRevisao]);

  const handleChangeHistory = useCallback(() => {
    if (selectedRevisao) {
      onOpenChangeHistory(Number(selectedRevisao.idProjetoRevisaoResumo), LogTipoTabela.PROJETO_REVISAO_RESUMO);
    }
  }, [selectedRevisao]);

  const handleSubmit = useCallback((form: ProjetoRevisaoResumoForm) => {
    if (!saving) {
      onSubmit(form);
    }
  }, [onSubmit, saving]);

  const handleClose = useCallback(() => {
    if (!saving) {
      onClose();
      hookForm.reset();
    }
  }, [hookForm, onClose, saving]);


  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "42rem",
          },
        },
      }}
    >
      <DialogTitle>
        Revisão do projeto
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={selectedRevisao ? "Editar" : "Cadastrar"} arrangement="fullRow">
            <>
              <DropDownFuncionario
                name='idFuncionario'
                label="Responsável"
                required={true}
                reactHookForm={hookForm}
              />
              <div className="md:col-span-2">
                <DefaultFormInput
                  name='descricao'
                  label="Descrição"
                  required={true}
                  multiline={true}
                  rows={4}
                  reactHookForm={hookForm}
                />
              </div>
              <FormDatePickerInput name="data" label="Data" reactHookForm={hookForm} required={true} />
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div className="w-[220px]">
            {
              isNotNullOrUndefined(selectedRevisao) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>
          <div className="flex flex-row gap-2 w-full justify-end">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}